import { setSizeToMediaUrl } from "@client/lib/media";
import { joinDefined } from "@client/lib/misc";
import { parseValue } from "@client/lib/values/size";
import {
  combinedStyleSelector,
  createShallowEqualSelector,
  entitiesSelector,
  getCompositeSettingSelector,
  getCornersRadiusSelector,
  idPropsSelector
} from "@client/selectors";
import { type CompositeSettings, EntityType } from "@client/types";
import React from "react";
import { createSelector } from "reselect";
import { type ImageExtraAttributes } from "./component";

const stylePropsSelector = (state: any, props: { style?: React.CSSProperties }) => props.style;

export const makeImageStyleSelector = (target: string, prefix?: string) => {
  const commonStyleSelector = combinedStyleSelector(target, ["border", "boxShadow"], joinDefined(".", prefix, "image"));
  const radiusSelector = getCornersRadiusSelector(target, joinDefined(".", prefix, "image"));

  const settingSelector = getCompositeSettingSelector<CompositeSettings.Image>(target, "image", prefix);
  return createShallowEqualSelector(
    [settingSelector, radiusSelector, commonStyleSelector],
    (setting, radius, style): React.CSSProperties | undefined => {
      if (!setting) return { ...style, borderRadius: radius };

      const useAspectRatio = parseValue(setting.size).useAspectRatio;
      return {
        ...style,
        objectFit: useAspectRatio ? undefined : setting.fit,
        objectPosition: useAspectRatio ? undefined : setting.position,
        borderRadius: radius
      };
    }
  );
};

export const makeExtraAttributesSelector = () => {
  return createSelector([entitiesSelector, idPropsSelector], (entities, id): ImageExtraAttributes => {
    let entity = entities[id];
    while (entity && entity.type !== EntityType.Block && entity.parentId) {
      entity = entities[entity.parentId];
    }

    if (!entity || !entity.parentId) return {};

    const parent = entities[entity.parentId];
    const index = parent.childrenIds!.indexOf(entity.id);
    return {
      fetchPriority: index < 2 ? "high" : undefined,
      loading: index >= 2 ? "lazy" : undefined
    };
  });
};

export const makeStyleSelector = (target: string, prefix?: string) => {
  const imageStyleSelector = makeImageStyleSelector(target, prefix);

  return createShallowEqualSelector(
    [imageStyleSelector, stylePropsSelector],
    (imageStyle, style): React.CSSProperties => ({
      ...imageStyle,
      ...style
    })
  );
};

export const makeSrcSelector = () =>
  createSelector(
    [(_, p: { src: string }) => p.src, (_, p: { size: string }) => p.size, (_, p: { fit?: string }) => p.fit],
    setSizeToMediaUrl
  );
